import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IObservableArray } from 'mobx';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { read } from 'xlsx';

import type { IImportColumn, Importer, TImporterState } from '@feathr/blackbox';
import { parseWorkbook } from '@feathr/blackbox';
import {
  Button,
  type ButtonType,
  ButtonValid,
  CardContent,
  CardHeader,
  CardV2,
  Fieldset,
  FileUpload,
  Form,
  Input,
  Radios,
} from '@feathr/components';
import TagsField from '@feathr/extender/components/TagsField';
import { useFlags } from '@feathr/extender/state';
import {
  MAX_CELL_CHARACTERS,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from '@feathr/extender/utils/importFileLimits';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';

import { isContactsOnlyImport } from './DataImportPage.utils';

import * as styles from './DataImportPage.css';

interface IProps {
  disabled?: boolean;
  importer: Importer;
  onNext: () => void;
  /** False when the page renders step navigation in its own action bar. */
  showActions?: boolean;
}

interface IButtonProps {
  importer: Importer;
  onNext: () => void;
  /** Button copy. Defaults to "Next"; the revamped wizard's action bar says "Continue". */
  label?: string;
  type?: ButtonType;
}

export const StepOneNextButton = observer(({ importer, label, onNext, type }: IButtonProps) => {
  const { t } = useTranslation();

  const validationErrors = validateStepOne(importer);
  if (importer.get('column_mappers').length < 1) {
    validationErrors.push(t('You must have at least 1 column that maps to Email.'));
  } else if (!importer.get('rows') || importer.get('rows') < 1) {
    validationErrors.push(t('You must have at least 1 row to import data.'));
  }
  return (
    <ButtonValid
      errors={validationErrors}
      id={'goToStepTwo'}
      name={'next_step'}
      onClick={onNext}
      type={type}
    >
      {label ?? t('Next')}
    </ButtonValid>
  );
});

export const validateStepOne = (importer: Importer): IObservableArray<string> => {
  return toJS(importer.validate(['name', 'file_name', 'data_key', 'tag_ids'], false).errors);
};

type TImportType = 'contacts' | 'opt_out';

// anhinga does not guard overrides by state, so the wizard locks the type once an import runs.
const TYPE_LOCKED_STATES = new Set<TImporterState>(['pending', 'complete']);

function DataImportStepOne({
  disabled = false,
  importer,
  onNext,
  showActions = true,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const { search } = useLocation();

  const isContactsOnly = isContactsOnlyImport(search);
  const showImportType = isRevampEnabled && !isContactsOnly;
  const isTypeDisabled = disabled || TYPE_LOCKED_STATES.has(importer.get('state'));
  const importType: TImportType = importer.isOptOut ? 'opt_out' : 'contacts';

  const importTypeOptions = [
    {
      id: 'contacts',
      name: t('Import contact data'),
      description: t('Add or update people and their fields.'),
    },
    {
      id: 'opt_out',
      name: t('Import opt out list'),
      description: t('Unsubscribe everyone in the file from all email campaigns.'),
    },
  ];

  const actions = showActions
    ? [<StepOneNextButton importer={importer} key={'nextStep'} onNext={onNext} />]
    : undefined;

  function setTagIds(val: string[]): void {
    importer.set({ tag_ids: val });
  }

  function handleChangeImportType(newValue?: string): void {
    if (!newValue || newValue === importType) {
      return;
    }
    const isOptOut = newValue === 'opt_out';
    importer.setOptOut(isOptOut);
    if (isOptOut) {
      // Tags are hidden on an opt-out import, but the run would still apply them.
      importer.set({ tag_ids: [] });
    }
    // Mapping fields differ by type, so keep the file but start mapping over.
    importer.set({
      column_mappers: observable.array(
        importer.get('column_mappers', []).map(
          (column) =>
            ({
              column_name: column.column_name,
              feathr_attr: undefined,
              attr_type: undefined,
            }) as IImportColumn,
        ),
      ),
    });
  }

  function handleUpload(key: string, container: string, file: File, filename: string): void {
    const reader = new FileReader();

    function onLoad(): void {
      if (reader.result && reader.result instanceof ArrayBuffer) {
        const array = new Uint8Array(reader.result);
        const workbook = read(array, { type: 'array' });
        const { columnNames: columns, rows: dataRows } = parseWorkbook(workbook, {
          detectHeaderless: isRevampEnabled,
          nameBlankHeaders: isRevampEnabled,
          renameRepeatedHeaders: isRevampEnabled,
        });

        importer.set({
          data_key: key,
          data_bucket: container,
          file_name: filename,
          name: importer.get('name'),
          column_names: columns,
          column_mappers: observable.array(
            columns.map(
              (columnName) =>
                ({
                  column_name: columnName,
                  feathr_attr: undefined,
                  attr_type: undefined,
                }) as IImportColumn,
            ),
          ),
          rows: dataRows.length,
        });
      }
    }

    reader.addEventListener('load', onLoad);
    reader.readAsArrayBuffer(file);
  }

  function clearFile(): void {
    importer.unset('data_key');
    importer.unset('data_bucket');
    importer.unset('file_name');
    importer.unset('column_names');
    importer.set({ column_mappers: [], rows: 0 });
  }

  const legacyDescription = importer.isOptOut
    ? t(
        'Upload a spreadsheet of email addresses to import as an opt out list. Every Person connected with each email address in your import will be unsubscribed from all of your email campaigns, whether they already exist in your account or are being imported for the first time. You may also include additional CRM data to attach to each opted out Person your import creates.',
      )
    : t(
        'Upload a spreadsheet of email addresses to import into your database as person records. You may also include additional CRM data to attach to each person identified by email address.',
      );
  const description = importer.isOptOut
    ? t('Choose the CSV or Excel file of email addresses to opt out')
    : t('Choose the CSV or Excel file of contacts to import');

  const fields = (
    <>
      {showImportType ? (
        <Fieldset>
          <Radios
            dataName={'import-type'}
            disabled={isTypeDisabled}
            label={t('What are you importing?')}
            layout={'block'}
            onChange={handleChangeImportType}
            options={importTypeOptions}
            value={importType}
          />
        </Fieldset>
      ) : null}
      <Fieldset>
        <Input
          attribute={'name'}
          disabled={disabled}
          label={t('Name')}
          model={importer}
          type={'text'}
        />
      </Fieldset>
      <Fieldset>
        {!importer.isOptOut && (
          <TagsField
            context={'person'}
            disabled={disabled}
            helpText={t(
              'Tagging is the best way to group your imports. Include one or more tags to ensure you can find your lists for future email campaigns.',
            )}
            label={t('Tags')}
            onChange={setTagIds}
            value={importer.get('tag_ids', [])}
          />
        )}
      </Fieldset>
      <Fieldset direction={'column'}>
        <FileUpload
          attribute={'file'}
          disabled={disabled}
          id={'upload'}
          model={importer}
          onUpload={handleUpload}
          pickerOptions={{
            accept: [
              'text/comma-separated-values',
              'text/csv',
              'application/csv',
              '.csv',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              '.xlsx',
            ],
            fromSources: [
              'local_file_system',
              'imagesearch',
              'facebook',
              'instagram',
              'googledrive',
            ],
            maxFiles: 1,
            maxSize: MAX_FILE_SIZE_BYTES,
            exposeOriginalFile: true,
            storeTo: {
              location: 's3',
              container: 'feathr-import-data',
              access: 'private',
              region: 'us-east-1',
            },
          }}
          wrapperClassName={styles.upload}
        />
        <div className={styles.filename}>
          <Input
            attribute={'file_name'}
            disabled={true}
            helpPlacement={'bottom'}
            helpText={[
              t(
                'Accepts .xlsx and .csv files up to {{maxFileSizeMb}} MB, with up to {{maxCellCharacters}} characters per cell.',
                {
                  maxCellCharacters: numeral(MAX_CELL_CHARACTERS).format('0,0'),
                  maxFileSizeMb: MAX_FILE_SIZE_MB,
                },
              ),
              // Only the orchestrator importer strips number symbols.
              isRevampEnabled &&
                t(
                  "Write numbers like 1,000.50 or $1,000. Numbers like 1.000,50, (250) or 15% can't be read and are left empty.",
                ),
            ]
              .filter(Boolean)
              .join(' ')}
            model={importer}
            suffix={
              importer.get('file_name') ? (
                <Button onClick={clearFile} type={'naked'}>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              ) : undefined
            }
            type={'text'}
          />
          {importer.get('column_mappers') && importer.get('column_mappers').length > 0 && (
            <p>
              {t('Number of columns: {{columnCount}}', {
                columnCount:
                  importer.get('column_mappers') && importer.get('column_mappers').length,
              })}
              {importer.get('column_mappers') && importer.get('column_mappers').length >= 1 ? (
                <FontAwesomeIcon className={styles.valid} icon={faCheck} />
              ) : (
                <FontAwesomeIcon className={styles.invalid} icon={faTimes} />
              )}
            </p>
          )}
          {importer.get('rows', 0) > 0 && (
            <p>
              {t('Number of rows: {{rowCount}}', {
                rowCount: numeral(importer.get('rows')).format('0,0'),
              })}
              {importer.get('rows') > 0 ? (
                <FontAwesomeIcon className={styles.valid} icon={faCheck} />
              ) : (
                <FontAwesomeIcon className={styles.invalid} icon={faTimes} />
              )}
            </p>
          )}
        </div>
      </Fieldset>
    </>
  );

  return (
    <Form
      actions={actions}
      // The revamped step's card header carries the description.
      description={isRevampEnabled ? undefined : legacyDescription}
      label={isRevampEnabled ? t('Upload file') : t('Upload')}
      width={isRevampEnabled ? 'wide' : 'narrow'}
    >
      {isRevampEnabled ? (
        <CardV2 name={'import-upload'} width={'full'}>
          <CardHeader title={description} />
          <CardContent>{fields}</CardContent>
        </CardV2>
      ) : (
        fields
      )}
    </Form>
  );
}

export default observer(DataImportStepOne);
